'use client';
import { BootTradeInSection } from '@c/bootTradeInSection';
import NewArrivals from '@c/newArrivals';
import { PopularCategories } from '@c/PopularCategories';
import Carousel from '@ui/Carousel';
import type { BlogDocument } from '@util/types/firestore/blog';
import { useAuth } from 'context/AuthContext';
import useMyFeed from 'features/homepage/hooks/useMyFeed';
import {
  injectLogoScript,
  injectSoftwareAppScript,
} from 'features/schemaMarkups';
import type { BannerDocument } from 'models/banner';
import type { CuratedListDocument } from 'models/curated_list';
import type { ProductLight } from 'models/product';
import type { TrendingSearch } from 'models/trending-search';
import Script from 'next/script';
import { renderProductCards } from '../../utils';
import CategoryLinks from '../CategoryLinks';
import CuratedLists from '../CuratedLists';
import GetTheAppCTA from '../GetTheAppCTA';
import Highlights from '../Highlights';
import LiveAuctions from '../LiveAuctions';
import { GearInYourSize, PartsForYourBikes } from '../MyFeed';
import RecentlySearched from '../RecentlySearched';
import RecentlyViewed from '../RecentlyViewed';
import ScrubDesignz from '../ScrubDesignz';
import SeeWhatOurCustomersSay from '../SeeWhatOurCustomersSay';
import ShopBySport from '../ShopBySport';
import ThePaddock from '../ThePaddock';
import WhyMX from '../WhyMX';
import HomepageCats from './components/HomepageCats';
import HomepageHeader from './components/HomepageHeader';
import HomeSection from './components/HomeSection';
import { TrendingSearches } from './components/TrendingSearches';
import ShopOemParts from './components/ShopOemParts';

type HomePageProps = {
  trendingProducts: ProductLight[];
  trendingSearches: TrendingSearch[];
  recentBikes: ProductLight[];
  onSale: ProductLight[];
  lists: CuratedListDocument[];
  banners: BannerDocument[];
  paddockPosts: BlogDocument[];
  liveAuctions: ProductLight[];
  trendingRidingGear: ProductLight[];
  trendingBikeParts: ProductLight[];
};

export default function Home(props: HomePageProps) {
  const { userDoc } = useAuth();

  const {
    trendingProducts,
    trendingSearches,
    lists,
    banners,
    paddockPosts,
    liveAuctions,
    recentBikes,
  } = props;

  const { data: myFeed, isLoading } = useMyFeed();
  const onSale = myFeed?.onsale?.length ? myFeed.onsale : props.onSale;

  const newArrivals = myFeed?.recent;

  return (
    <>
      {userDoc === null && (
        <Script
          async
          id="klaviyo-popup"
          type="text/javascript"
          src="https://static.klaviyo.com/onsite/js/Nk4Jwu/klaviyo.js"
        />
      )}

      <Script
        id="androidAppSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            injectSoftwareAppScript({
              name: 'MX Locker',
              operatingSystem: 'ANDROID',
              applicationCategory: 'Application',
              ratingValue: '4.9',
              ratingCount: '1000',
              price: '0.00',
              priceCurrency: 'USD',
            }) ?? '',
        }}
      />
      <Script
        id="iosAppSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            injectSoftwareAppScript({
              name: 'MX Locker',
              operatingSystem: 'IOS',
              applicationCategory: 'Application',
              ratingValue: '4.9',
              ratingCount: '1000',
              price: '0.00',
              priceCurrency: 'USD',
            }) ?? '',
        }}
      />

      <Script
        id="logoSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectLogoScript(
            'https://mxlocker.com/',
            'https://mxlocker.com/assets/mx-logo.png'
          ),
        }}
      />

      <HomepageCats />
      <HomepageHeader banners={banners} />
      <Highlights />

      <div className="mt-8 flex h-full w-full flex-col gap-y-[3rem] pt-8 sm:gap-y-[6rem] sm:pt-16">
        <LiveAuctions />

        {userDoc && (
          <HomeSection>
            <RecentlyViewed />
          </HomeSection>
        )}

        {userDoc && (
          <HomeSection>
            <RecentlySearched />
          </HomeSection>
        )}

        {!userDoc && (
          <HomeSection
            title="Trending Products"
            linkText="See All"
            href="/shop/all"
            className="pl-8 sm:pl-0"
          >
            <Carousel
              spacing={0}
              basis="basis-[50%] sm:basis-auto"
              items={renderProductCards(trendingProducts, {
                data_source: 'firebase',
                page: 'home',
                section: 'trending-products',
              })}
            />
          </HomeSection>
        )}

        {!!newArrivals?.length && newArrivals.length >= 6 && userDoc && (
          <HomeSection
            title="New Arrivals"
            subtitle="Curated for you."
            linkText="See All"
            href="/shop/recent"
          >
            <NewArrivals
              products={myFeed?.recent ?? []}
              isFetching={isLoading}
              smallProducts={false}
            />
          </HomeSection>
        )}

        <TrendingSearches trendingSearches={trendingSearches} />

        <PopularCategories />

        <div className="flex w-full flex-col items-center gap-16 px-4 pt-32 sm:mx-auto sm:max-w-[150rem] sm:flex-row sm:justify-between">
          <BootTradeInSection />
        </div>

        {userDoc && (
          <>
            <GearInYourSize />
          </>
        )}

        {userDoc && (
          <>
            <PartsForYourBikes />
          </>
        )}

        {!userDoc && !!props.trendingRidingGear?.length && (
          <HomeSection
            title="Trending in Gear"
            linkText="See All"
            href="/shop/riding-gear"
            className="pl-8 sm:pl-0"
          >
            <Carousel
              spacing={0}
              basis="basis-[50%] sm:basis-auto"
              items={renderProductCards(props.trendingRidingGear, {
                data_source: 'firebase',
                page: 'home',
                section: 'trending-riding-gear',
              })}
            />
          </HomeSection>
        )}

        {!userDoc && !!props.trendingBikeParts?.length && (
          <HomeSection
            title="Trending in Parts"
            linkText="See All"
            href="/shop/dirt-bike-parts"
            className="pl-8 sm:pl-0"
          >
            <Carousel
              spacing={0}
              basis="basis-[50%] sm:basis-auto"
              items={renderProductCards(props.trendingBikeParts, {
                data_source: 'firebase',
                page: 'home',
                section: 'trending-bike-parts',
              })}
            />
          </HomeSection>
        )}

        <HomeSection
          title="Shop By Sport"
          linkText="See All"
          href="/shop/all"
          className={`mx-auto mb-8 sm:mb-0`}
        >
          <ShopBySport variant="main" />
        </HomeSection>

        <HomeSection
          title="Price Drops"
          linkText="See All"
          href="/shop/sale"
          className="pl-8 sm:pl-0"
        >
          <Carousel
            spacing={0}
            basis="basis-[50%] sm:basis-auto"
            items={renderProductCards(onSale, {
              data_source: 'firebase',
              page: 'home',
              section: 'on-sale',
            })}
          />
        </HomeSection>

        <ScrubDesignz />

        <HomeSection title="Shop OEM Parts">
          <ShopOemParts />
        </HomeSection>

        {/* <HomeSection title="Shop By Brand" href={'/brands'} linkText="See All">
          <ShopByBrand />
        </HomeSection> */}

        <WhyMX />

        <HomeSection
          title="Dirt Bikes For Sale"
          href={'/dirt-bikes-for-sale'}
          linkText="See All"
          className="pl-8 sm:pl-0"
        >
          <Carousel
            spacing={0}
            basis="basis-[50%] sm:basis-auto"
            items={renderProductCards(
              recentBikes,
              {
                data_source: 'firebase',
                page: 'home',
                section: 'recent_bikes',
              },
              {
                page: 'home',
                section: 'recent_bikes',
              }
            )}
          />
        </HomeSection>

        <HomeSection title="The Paddock" className="mx-auto max-w-[150rem]">
          <ThePaddock posts={paddockPosts} />
        </HomeSection>

        <HomeSection title="Curated by MX Locker" className="mx-auto">
          <div className="overflow-x-auto scrollbar-none sm:flex sm:justify-center">
            <div className="flex w-max sm:w-auto">
              <CuratedLists lists={lists} />
            </div>
          </div>
        </HomeSection>

        <SeeWhatOurCustomersSay />

        <GetTheAppCTA />
      </div>

      <CategoryLinks />
    </>
  );
}
